// Backend Entity Names (mirroring discovery/server/src/ugc/schema/ugc-assessment.schema.ts)
export const ENTITY_NAME = {
  QUEST: "QUEST",
  LISTING: "LISTING",
  CONVERSATIONS: "CONVERSATIONS",
  PROFILE: "PROFILE",
};

// Backend Entity Actions (mirroring discovery/server/src/ugc/schema/ugc-assessment.schema.ts)
export const ENTITY_ACTION = {
  // Quest actions
  QUEST_QUESTION: "QUEST.QUESTION",
  QUEST_ANSWER: "QUEST.ANSWER",
  QUEST_COMMENT: "QUEST.COMMENT",
  // Listing actions
  LISTING_REVIEW: "LISTING.REVIEW",
  LISTING_QUESTION: "LISTING.QUESTION",
  // Conversation actions
  CONVERSATIONS_CHAT: "CONVERSATIONS.CHAT",
  CONVERSATIONS_POLL: "CONVERSATIONS.POLL", // Fixed to match entity name consistently
  // Profile actions
  PROFILE_NAME: "PROFILE.NAME",
  PROFILE_TAGLINE: "PROFILE.TAGLINE",
  PROFILE_BIO: "PROFILE.BIO",
  PROFILE_EXPERIENCE: "PROFILE.EXPERIENCE",
  VIRTUAL_OFFICE_NAME: "VIRTUAL_OFFICE.NAME",
  VIRTUAL_OFFICE_COURSE: "VIRTUAL_OFFICE.COURSE",
  VIRTUAL_OFFICE_UNIVERSITY: "VIRTUAL_OFFICE.UNIVERSITY",
  VIRTUAL_OFFICE_CITY: "VIRTUAL_OFFICE.CITY",
  VIRTUAL_OFFICE_TEST: "VIRTUAL_OFFICE.TEST",
  VIRTUAL_OFFICE_BANK: "VIRTUAL_OFFICE.BANK",
};

// Entity structure interface (mirroring backend)
// Note: This is a type definition, not an object with all values
// Usage example:
// const entity = {
//   name: ENTITY_NAME.CONVERSATIONS,
//   action: ENTITY_ACTION.CONVERSATIONS_POLL
// };

// Content type to entity action mapping
export const CONTENT_TYPE_TO_ENTITY_ACTION = {
  // Quest content types
  QUEST_DESCRIPTION: ENTITY_ACTION.QUEST_QUESTION,
  QUEST_QUESTION: ENTITY_ACTION.QUEST_QUESTION,

  // Answer content types
  ANSWER_DESCRIPTION: ENTITY_ACTION.QUEST_ANSWER,

  // Comment content types
  COMMENT_DESCRIPTION: ENTITY_ACTION.QUEST_COMMENT,

  // Message content types
  MESSAGE_DESCRIPTION: ENTITY_ACTION.CONVERSATIONS_CHAT,
  MESSAGE: ENTITY_ACTION.CONVERSATIONS_CHAT,

  // Review content types
  REVIEW_DESCRIPTION: ENTITY_ACTION.LISTING_REVIEW,

  // Poll content types
  POLL_QUESTION: ENTITY_ACTION.CONVERSATIONS_POLL,
  POLL_OPTION: ENTITY_ACTION.CONVERSATIONS_POLL,

  // Profile content types
  PROFILE_FIRST_NAME: ENTITY_ACTION.PROFILE_NAME,
  PROFILE_LAST_NAME: ENTITY_ACTION.PROFILE_NAME,
  PROFILE_TAGLINE: ENTITY_ACTION.PROFILE_TAGLINE,
  PROFILE_BIO: ENTITY_ACTION.PROFILE_BIO,
  PROFILE_WORK_POSITION: ENTITY_ACTION.PROFILE_EXPERIENCE,
  PROFILE_WORK_COMPANY: ENTITY_ACTION.PROFILE_EXPERIENCE,
  PROFILE_VIRTUAL_OFFICE_NAME: ENTITY_ACTION.VIRTUAL_OFFICE_NAME,
  VIRTUAL_OFFICE_COURSE: ENTITY_ACTION.VIRTUAL_OFFICE_COURSE,
  VIRTUAL_OFFICE_UNIVERSITY: ENTITY_ACTION.VIRTUAL_OFFICE_UNIVERSITY,
  VIRTUAL_OFFICE_CITY: ENTITY_ACTION.VIRTUAL_OFFICE_CITY,
  VIRTUAL_OFFICE_TEST: ENTITY_ACTION.VIRTUAL_OFFICE_TEST,
  VIRTUAL_OFFICE_BANK: ENTITY_ACTION.VIRTUAL_OFFICE_BANK,
};

// Service name to entity name mapping
export const SERVICE_NAME_TO_ENTITY_NAME = {
  quest_creation: ENTITY_NAME.QUEST,
  quest_updation: ENTITY_NAME.QUEST,
  message_creation: ENTITY_NAME.CONVERSATIONS,
  message_send: ENTITY_NAME.CONVERSATIONS,
  comment_creation: ENTITY_NAME.QUEST,
  answer_creation: ENTITY_NAME.QUEST,
  review_creation: ENTITY_NAME.LISTING,
  profile_update: ENTITY_NAME.PROFILE,
  poll_creation: ENTITY_NAME.CONVERSATIONS,
  virtual_office_name: ENTITY_NAME.PROFILE,
  virtual_office_course: ENTITY_NAME.PROFILE,
  virtual_office_university: ENTITY_NAME.PROFILE,
  virtual_office_city: ENTITY_NAME.PROFILE,
  virtual_office_test: ENTITY_NAME.PROFILE,
  virtual_office_bank: ENTITY_NAME.PROFILE,
};

// Spam severity levels for frontend handling
export const SPAM_SEVERITY = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical",
};

// Spam error messages have been moved to SpamWarningMessage.jsx as JSX templates

// Error severity levels for general error handling
export const ERROR_SEVERITY = {
  LOW: "low", // Minor issue, can be ignored
  MEDIUM: "medium", // Important but not critical
  HIGH: "high", // Critical issue
};

// Map of API endpoints to their criticality
export const ENDPOINT_CRITICALITY = {
  "conversation.list": ERROR_SEVERITY.HIGH,
  "conversation.history": ERROR_SEVERITY.HIGH,
  "conversation.mark": ERROR_SEVERITY.LOW,
  "conversation.participants": ERROR_SEVERITY.MEDIUM,
  "conversation.handleRoomJoinException": ERROR_SEVERITY.MEDIUM,
  "conversation.handleRoomJoinExceptionBatch": ERROR_SEVERITY.MEDIUM,
  quest: ERROR_SEVERITY.HIGH, // Quest creation/update is critical
  // Add more endpoints as needed
};

// HTML sanitization constants
export const ALLOWED_HTML_TAGS = {
  a: ["href", "target", "rel"],
  strong: [],
  em: [],
  b: [],
  i: [],
  u: [],
  br: [],
  span: ["class"],
  p: ["class"],
};

export const ALLOWED_PROTOCOLS = ["http:", "https:", "mailto:", "tel:"];

// Spam warning component styling constants
export const SPAM_WARNING_STYLES = {
  // CSS classes
  CONTAINER_CLASS:
    "flex items-center space-x-2 bg-red-50 border border-red-200 rounded-md px-3 py-2",
  TEXT_CLASS: "text-sm text-red-700",
  TEXT_CLASS_WITH_HTML: "text-sm text-red-700 spam-warning",

  // Inline styles
  LINK_STYLES: {
    textDecoration: "underline !important",
    color: "#dc2626 !important",
    cursor: "pointer !important",
    fontWeight: "500 !important",
  },
  LINK_HOVER_STYLES: {
    color: "#991b1b !important",
    textDecoration: "none !important",
  },
  TEXT_STYLES: {
    wordBreak: "break-word",
  },

  // Colors
  COLORS: {
    RED_50: "bg-red-50",
    RED_200: "border-red-200",
    RED_700: "text-red-700",
    LINK_RED: "#dc2626",
    LINK_HOVER_RED: "#991b1b",
  },
};

// Community guidelines URL
export const COMMUNITY_GUIDELINES_URL =
  "https://nbyula.com/community-guidelines";

// Default spam check response
export const DEFAULT_SPAM_CHECK_RESPONSE = {
  isSpam: false,
  severity: SPAM_SEVERITY.LOW,
  message: null,
  canProceed: true,
};

// Toast notification settings for spam warnings
export const SPAM_TOAST_SETTINGS = {
  position: "bottom-left",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

// Console log messages for spam detection
export const SPAM_LOG_MESSAGES = {
  LOW_SEVERITY_COMMENT:
    "Low severity spam detected in comment - allowing to proceed silently",
  LOW_SEVERITY_ANSWER:
    "Low severity spam detected in answer - allowing to proceed silently",
  LOW_SEVERITY_MESSAGE:
    "Low severity spam detected in message - allowing to proceed silently",
  LOW_SEVERITY_REVIEW:
    "Low severity spam detected in review - allowing to proceed silently",
  LOW_SEVERITY_POLL:
    "Low severity spam detected in poll question - allowing to proceed silently",
  SPAM_CHECK_ERROR: "Spam check error:",
  UNKNOWN_SERVICE:
    "Unknown serviceName: {serviceName}, using default QUEST entity",
  UNKNOWN_CONTENT_TYPE:
    "Unknown contentType: {contentType}, using default QUEST.QUESTION action",
  INVALID_URL: "Invalid URL in spam warning message:",
  UGC_ASSESSMENT_FAILED: "UGC assessment check failed:",
  PERSPECTIVE_API_ERROR: "Perspective API error:",
  PERSPECTIVE_API_KEY_MISSING: "Perspective API key not configured",
};

// Perspective API configuration
export const PERSPECTIVE_API_CONFIG = {
  REQUESTED_ATTRIBUTES: {
    TOXICITY: {},
    SEVERE_TOXICITY: {},
    IDENTITY_ATTACK: {},
    INSULT: {},
    PROFANITY: {},
    THREAT: {},
  },
  THRESHOLDS: {
    CRITICAL_MAX_SCORE: 0.8,
    CRITICAL_AVG_SCORE: 0.6,
    HIGH_MAX_SCORE: 0.6,
    HIGH_AVG_SCORE: 0.4,
    MEDIUM_MAX_SCORE: 0.4,
    MEDIUM_AVG_SCORE: 0.2,
    MEDIUM_SPAM_THRESHOLD: 0.3,
  },
};

// Content type constants for different forms
export const CONTENT_TYPES = {
  QUEST: {
    DESCRIPTION: "QUEST_DESCRIPTION",
    QUESTION: "QUEST_QUESTION",
  },
  ANSWER: {
    DESCRIPTION: "ANSWER_DESCRIPTION",
  },
  COMMENT: {
    DESCRIPTION: "COMMENT_DESCRIPTION",
  },
  MESSAGE: {
    DESCRIPTION: "MESSAGE_DESCRIPTION",
    TEXT: "MESSAGE",
  },
  REVIEW: {
    DESCRIPTION: "REVIEW_DESCRIPTION",
  },
  POLL: {
    QUESTION: "POLL_QUESTION",
    OPTION: "POLL_OPTION",
  },
  PROFILE: {
    FIRST_NAME: "PROFILE_FIRST_NAME",
    LAST_NAME: "PROFILE_LAST_NAME",
    TAGLINE: "PROFILE_TAGLINE",
    BIO: "PROFILE_BIO",
    WORK_POSITION: "PROFILE_WORK_POSITION",
    WORK_COMPANY: "PROFILE_WORK_COMPANY",
    VIRTUAL_OFFICE_NAME: "PROFILE_VIRTUAL_OFFICE_NAME",
  },
  VIRTUAL_OFFICE: {
    COURSE: "VIRTUAL_OFFICE_COURSE",
    UNIVERSITY: "VIRTUAL_OFFICE_UNIVERSITY",
    CITY: "VIRTUAL_OFFICE_CITY",
    TEST: "VIRTUAL_OFFICE_TEST",
    BANK: "VIRTUAL_OFFICE_BANK",
  },
};

// Service names for different actions
export const SERVICE_NAMES = {
  QUEST_CREATION: "quest_creation",
  QUEST_UPDATION: "quest_updation",
  MESSAGE_CREATION: "message_creation",
  MESSAGE_SEND: "message_send",
  COMMENT_CREATION: "comment_creation",
  ANSWER_CREATION: "answer_creation",
  REVIEW_CREATION: "review_creation",
  PROFILE_UPDATE: "profile_update",
  POLL_CREATION: "poll_creation",
  VIRTUAL_OFFICE_NAME: "virtual_office_name",
  VIRTUAL_OFFICE_COURSE: "virtual_office_course",
  VIRTUAL_OFFICE_UNIVERSITY: "virtual_office_university",
  VIRTUAL_OFFICE_CITY: "virtual_office_city",
  VIRTUAL_OFFICE_TEST: "virtual_office_test",
  VIRTUAL_OFFICE_BANK: "virtual_office_bank",
};
